import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Minus, Filter, Activity, Heart, Clock, AlertCircle } from 'lucide-react';
import { reportService } from '@/services/reportService';
import { useToast } from '@/hooks/use-toast';

interface KPI {
  value: number;
  benchmark: number;
  status: 'above' | 'at' | 'below';
}

export const QualityIndicatorsReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hospitalId, setHospitalId] = useState('all');
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (hospitalId && hospitalId !== 'all') filters.hospital_id = parseInt(hospitalId);

      const response = await reportService.getQualityIndicators(filters);
      setData(response);
    } catch (error) {
      console.error('Error fetching quality indicators:', error);
      toast({ title: 'Eroare', description: 'Nu s-au putut încărca indicatorii de calitate', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusIcon = (status: string) => {
    if (status === 'above') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (status === 'below') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-600" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'above') return 'text-green-600';
    if (status === 'below') return 'text-red-600';
    return 'text-gray-600';
  };

  const getStatusBadge = (status: string) => {
    if (status === 'above') return 'success';
    if (status === 'below') return 'destructive';
    return 'secondary';
  };

  const renderKPI = (title: string, kpi: KPI, unit: string, icon: any) => {
    const Icon = icon;
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-1">{title}</p>
              <div className="flex items-baseline gap-2">
                <p className={`text-3xl font-bold ${getStatusColor(kpi.status)}`}>
                  {kpi.value.toFixed(1)}{unit}
                </p>
                {getStatusIcon(kpi.status)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Țintă: {kpi.benchmark.toFixed(1)}{unit}
              </p>
            </div>
            <Icon className="h-8 w-8 text-muted-foreground" />
          </div>
          <Progress value={(kpi.value / kpi.benchmark) * 100} className="h-2" />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" />Filtre raport</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hospital">Spital</Label>
              <Select value={hospitalId} onValueChange={setHospitalId}>
                <SelectTrigger id="hospital"><SelectValue placeholder="Toate spitalele" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate spitalele</SelectItem>
                  <SelectItem value="1">Spital 1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full"><Filter className="h-4 w-4 mr-2" />Aplică filtre</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}

      {!loading && data && data.kpis && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderKPI('Rata de supraviețuire', data.kpis.survival_rate, '%', Heart)}
            {renderKPI('Rata de complicații', data.kpis.complication_rate, '%', AlertCircle)}
            {renderKPI('Durata medie de spitalizare', data.kpis.average_length_of_stay, ' zile', Clock)}
            {renderKPI('Rata de readmisie', data.kpis.readmission_rate, '%', Activity)}
            {renderKPI('Rata de succes proceduri', data.kpis.procedural_success_rate, '%', TrendingUp)}
          </div>

          {data.benchmarking && (
            <Card>
              <CardHeader><CardTitle>Comparație cu media națională</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Performanța spitalului</p>
                      <p className="text-2xl font-bold text-blue-600">{data.benchmarking.hospital_performance?.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="font-medium">Media națională</p>
                      <p className="text-2xl font-bold text-gray-600">{data.benchmarking.national_average?.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="font-medium">Percentilă</p>
                      <p className="text-2xl font-bold text-green-600">{data.benchmarking.percentile_rank}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!loading && (!data || !data.kpis) && (
        <Alert><AlertCircle className="h-4 w-4" /><AlertDescription>Nu există date disponibile pentru indicatorii de calitate</AlertDescription></Alert>
      )}
    </div>
  );
};
